import type { GamakaProfileEntry } from '@swarasense/shared';

/**
 * Pure coordinate mappers for the pitch ribbon (Task 4's replacement for the
 * ladder). Kept free of Canvas/DOM so they're trivially unit-testable — see
 * `ribbon-geometry.test.ts` — and reused by `ribbon.ts` for both drawing and
 * hit-testing.
 */

/** The vertical scale: a cents range mapped onto a pixel band with top/bottom padding. */
export interface RibbonView {
  minCents: number;
  maxCents: number;
  height: number;
  pad: number;
}

/** The horizontal (time) scale: a trailing window of `windowMs` mapped across `width` px. */
export interface TimeView {
  windowMs: number;
  width: number;
}

export interface TargetBand {
  sym: string;
  y: number;
}

export interface GamakaZone {
  sym: string;
  /** The higher-cents edge — smaller y (nearer the top of the canvas). */
  yTop: number;
  /** The lower-cents edge — larger y (nearer the bottom of the canvas). */
  yBottom: number;
}

/** Symmetric half-width (cents) used when a gamaka entry gives neither
 * `oscillation_range_cents` nor `tolerance_cents` — wide enough to read as a
 * real oscillation band, narrower than the ~65¢ in-raga band so it doesn't
 * swallow a neighbouring swara's zone. */
const DEFAULT_ZONE_HALF_WIDTH_CENTS = 30;

/** Cents → y. Higher cents map to a smaller y (nearer the top), so the pitch
 * ribbon reads bottom (low)-to-top (high) like the ladder it replaces.
 * Monotonic and invertible over any non-degenerate `view`. */
export function centsToY(cents: number, view: RibbonView): number {
  const { minCents, maxCents, height, pad } = view;
  const usable = Math.max(1, height - 2 * pad);
  const span = maxCents - minCents || 1;
  const frac = (cents - minCents) / span;
  return height - pad - frac * usable;
}

/** Time → x. `nowMs` sits at the right edge (`width`); a frame `windowMs` old
 * sits at the left edge (`0`) — older frames scroll further left as time advances. */
export function timeToX(tMs: number, nowMs: number, view: TimeView): number {
  const { windowMs, width } = view;
  const ageMs = nowMs - tMs;
  return width - (ageMs / windowMs) * width;
}

/** One horizontal reference line per swara in the current scale, at its target cents. */
export function targetBands(targetCents: Record<string, number>, view: RibbonView): TargetBand[] {
  return Object.entries(targetCents).map(([sym, cents]) => ({ sym, y: centsToY(cents, view) }));
}

/** A swara "oscillates" (takes a gamaka rather than a plain hold) when its
 * profile isn't `steady`, or explicitly disallows holding — mirrors the same
 * predicate `analyzeSession` uses to decide held-flat/wavered verdicts. */
function isOscillating(entry: GamakaProfileEntry): boolean {
  return entry.style !== 'steady' || entry.hold_allowed === false;
}

/**
 * Shaded gamaka zones for oscillating swaras only — a steady swara (or one
 * with no profile entry at all) gets no zone. Sized from the entry's
 * `oscillation_range_cents` (a signed [lo, hi] offset from the target, per the
 * seed schema) when present; otherwise from `tolerance_cents` as a symmetric
 * half-width, or `DEFAULT_ZONE_HALF_WIDTH_CENTS` when neither is given.
 */
export function gamakaZones(
  gamakaProfile: Record<string, GamakaProfileEntry>,
  targetCents: Record<string, number>,
  view: RibbonView,
): GamakaZone[] {
  const zones: GamakaZone[] = [];
  for (const [sym, target] of Object.entries(targetCents)) {
    const entry = gamakaProfile[sym];
    if (!entry || !isOscillating(entry)) continue;
    let loCents: number;
    let hiCents: number;
    if (entry.oscillation_range_cents) {
      const [a, b] = entry.oscillation_range_cents;
      loCents = target + Math.min(a, b);
      hiCents = target + Math.max(a, b);
    } else {
      const half = entry.tolerance_cents ?? DEFAULT_ZONE_HALF_WIDTH_CENTS;
      loCents = target - half;
      hiCents = target + half;
    }
    zones.push({ sym, yTop: centsToY(hiCents, view), yBottom: centsToY(loCents, view) });
  }
  return zones;
}
